import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../../shared/grid/usePersistedGridState';
import { IncludeInactiveToggle } from '../../../../shared/components/IncludeInactiveToggle';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { useToast } from '../../../../shared/stores/toast.store';
import { describeApiError } from '../../../../shared/api/errors';
import { tiposTarefaHooks } from '../hooks';
import { tiposTarefaApi } from '../api';
import { TipoTarefaFormDialog } from '../TipoTarefaFormDialog';
import type { TipoTarefaDto, TipoTarefaListItem } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'nome',
  sortOrder: 'asc',
};

export function TiposTarefaListPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('tipos-tarefa', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<TipoTarefaDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<TipoTarefaListItem | null>(null);

  const listQuery = tiposTarefaHooks.useList(query);
  const removeMutation = tiposTarefaHooks.useDelete();

  async function openEdit(row: TipoTarefaListItem): Promise<void> {
    try {
      setEditing(await tiposTarefaApi.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.tiposTarefa.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<TipoTarefaListItem>[] = [
    { field: 'nome', headerName: t('features.tiposTarefa.columns.nome'), flex: 1, minWidth: 220 },
    {
      field: 'relacaoHora',
      headerName: t('features.tiposTarefa.columns.relacaoHora'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | null) =>
        value === null || value === undefined
          ? '—'
          : value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      field: 'ativo',
      headerName: t('features.tiposTarefa.columns.ativo'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t('features.tiposTarefa.status.active')
              : t('features.tiposTarefa.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.tiposTarefa.actions.edit', { nome: params.row.nome })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.tiposTarefa.actions.delete', { nome: params.row.nome })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.taskTypes')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.taskTypes')}
        breadcrumbs={[{ label: t('menu.generalRegistries') }, { label: t('menu.taskTypes') }]}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.tiposTarefa.actions.new')}
          </Button>
        }
      />
      <DataTable<TipoTarefaListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.tiposTarefa.searchPlaceholder')}
        filters={<IncludeInactiveToggle query={query} onChange={setQuery} />}
      />
      <TipoTarefaFormDialog
        open={editing !== null}
        tipoTarefa={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.tiposTarefa.deleteConfirm.title')}
        description={t('features.tiposTarefa.deleteConfirm.description', {
          nome: pendingDelete?.nome ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={removeMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
